import { useCallback, useMemo, useState } from 'react'
import { KVMarkup } from 'types'

interface UseMarkupVisibilityProps<T extends KVMarkup> {
  items: T[] | undefined
  updateFunction: (
    item: T,
    field: string | string[],
    setter: (item: T) => T,
    value: unknown | unknown[]
  ) => void
  visibilityField?: string
}

export function useMarkupVisibility<T extends KVMarkup>({
  items,
  updateFunction,
  visibilityField = 'visible',
}: UseMarkupVisibilityProps<T>) {
  const [showAllState, setShowAllState] = useState(true)

  // Check if all items are visible
  const allVisible = useMemo(() => {
    if (!items || items.length === 0) return true
    return items.every(
      (item) =>
        (item as unknown as Record<string, unknown>)[visibilityField] as boolean
    )
  }, [items, visibilityField])

  // Check if all items are hidden
  const allHidden = useMemo(() => {
    if (!items || items.length === 0) return false
    return items.every(
      (item) =>
        !((item as unknown as Record<string, unknown>)[
          visibilityField
        ] as boolean)
    )
  }, [items, visibilityField])

  // Show all items
  const showAll = useCallback(() => {
    if (!items) return

    items.forEach((item) => {
      if (
        !((item as unknown as Record<string, unknown>)[
          visibilityField
        ] as boolean)
      ) {
        updateFunction(
          item,
          visibilityField,
          (item: T) => {
            ;(item as unknown as Record<string, unknown>)[visibilityField] =
              true
            return item
          },
          true as unknown
        )
      }
    })
    setShowAllState(true)
  }, [items, updateFunction, visibilityField])

  // Hide all items
  const hideAll = useCallback(() => {
    if (!items) return

    items.forEach((item) => {
      if (
        (item as unknown as Record<string, unknown>)[visibilityField] as boolean
      ) {
        updateFunction(
          item,
          visibilityField,
          (item: T) => {
            ;(item as unknown as Record<string, unknown>)[visibilityField] =
              false
            return item
          },
          false as unknown
        )
      }
    })
    setShowAllState(false)
  }, [items, updateFunction, visibilityField])

  // Toggle all visibility
  const toggleAll = useCallback(() => {
    if (allVisible) {
      hideAll()
    } else {
      showAll()
    }
  }, [allVisible, showAll, hideAll])

  return {
    showAllState,
    allVisible,
    allHidden,
    showAll,
    hideAll,
    toggleAll,
  }
}
